import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, MapPin, Calendar, Package, Filter, Search, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';

export default function JobListings() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const res = await apiClient.get('/jobs');
      setJobs(res.data.filter(j => j.status === 'pending' || j.status === 'quoted'));
    } catch (error) {
      console.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.pickup_city.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.delivery_city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || job.job_type === filterType;
    return matchesSearch && matchesType;
  });

  const getJobTypeBadge = (type) => {
    const badges = {
      household: 'bg-green-100 text-green-700',
      freight: 'bg-blue-100 text-blue-700',
      cargo: 'bg-purple-100 text-purple-700',
    };
    return badges[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="btn-accent" data-testid="dashboard-link">Dashboard</Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="btn-accent" data-testid="login-link">Get Started</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
          <h1 className="text-3xl font-bold">Available Moving Jobs</h1>
          <p className="text-slate-300 mt-2">Browse and bid on moving jobs posted by customers</p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by location, title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="household">Household</SelectItem>
                <SelectItem value="freight">Freight</SelectItem>
                <SelectItem value="cargo">Cargo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No jobs found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.map((job) => (
              <div key={job.id} className="job-card" data-testid={`job-card-${job.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className={`badge ${getJobTypeBadge(job.job_type)}`}>
                    {job.job_type}
                  </span>
                  <span className="text-sm text-slate-500">
                    {job.quote_count} quotes
                  </span>
                </div>
                
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{job.title}</h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{job.description}</p>
                
                <div className="space-y-2 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-green-500" />
                    <span>{job.pickup_city}, {job.pickup_state}</span>
                    <span className="text-slate-300">→</span>
                    <span>{job.delivery_city}, {job.delivery_state}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span>{format(new Date(job.pickup_date), 'MMM d, yyyy')}</span>
                  </div>
                </div>
                
                {user?.role === 'carrier' ? (
                  <Link to="/carrier/dashboard">
                    <Button className="w-full btn-primary" data-testid={`submit-quote-${job.id}`}>
                      Submit Quote
                    </Button>
                  </Link>
                ) : (
                  <Link to="/auth?role=carrier">
                    <Button className="w-full btn-secondary">
                      Sign in to Quote
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
